import React, { useMemo } from "react";
import { ChevronRight, Sparkles } from "lucide-react";

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString()}`;
const fmtRoas = (v) => Number(v || 0).toFixed(1);

// Build a forest of trees from a node set + parent edges. Nodes whose
// parent isn't in the set become roots. Cycles are guarded against.
const buildForest = (nodes = [], edges = []) => {
  const byId = new Map();
  nodes.forEach((n) => byId.set(n.id, { ...n, _children: [] }));
  const parentOf = new Map();
  edges.forEach((e) => {
    if (e.type !== "parent") return;
    if (!byId.has(e.source) || !byId.has(e.target)) return;
    parentOf.set(e.target, e.source);
  });
  // also honour node.parent / node.parent_concept_id when edges weren't built
  nodes.forEach((n) => {
    if (parentOf.has(n.id)) return;
    const p = n.parent_concept_id || n.parent;
    if (p && byId.has(p)) parentOf.set(n.id, p);
  });

  const roots = [];
  byId.forEach((node, id) => {
    const parent = parentOf.get(id);
    if (parent && parent !== id) {
      const parentNode = byId.get(parent);
      if (parentNode) parentNode._children.push(node);
      else roots.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortByMetric = (a, b) => (b.spend || 0) - (a.spend || 0);
  const sortRecursive = (n) => {
    if (n._children?.length) {
      n._children.sort(sortByMetric);
      n._children.forEach(sortRecursive);
    }
  };
  roots.sort(sortByMetric);
  roots.forEach(sortRecursive);
  return roots;
};

const TreeNode = ({ node, onSelect, selectedId }) => {
  const hasChildren = node._children?.length > 0;
  const isSelected = selectedId === node.id;
  const tone =
    (node.roas || 0) >= 4 ? "good" : (node.roas || 0) < 2 ? "bad" : "neutral";
  return (
    <li className="web-tree__item">
      <button
        type="button"
        className={`web-tree__row web-tree__row--${tone}${isSelected ? " web-tree__row--selected" : ""}`}
        onClick={() => onSelect(node.id)}
      >
        <span className="web-tree__chevron">
          {hasChildren ? <ChevronRight size={12} /> : <span style={{ width: 12 }} />}
        </span>
        <span className="web-tree__label" title={node.label}>
          {node.label}
        </span>
        <span className="web-tree__metric web-tree__metric--spend">{fmtMoney(node.spend)}</span>
        <span className={`web-tree__metric web-tree__metric--roas web-tree__metric--${tone}`}>
          {fmtRoas(node.roas)}x
        </span>
        {node.scaling ? (
          <span className="web-tree__chip web-tree__chip--scale">
            <Sparkles size={10} /> scaling
          </span>
        ) : null}
      </button>
      {hasChildren ? (
        <ul className="web-tree__children">
          {node._children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              onSelect={onSelect}
              selectedId={selectedId}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
};

export default function WebTreeView({ nodes, edges, selectedId, onSelect }) {
  const filtered = useMemo(
    () => (nodes || []).filter((n) => n.type === "concept" || n.type === "variation"),
    [nodes],
  );
  const forest = useMemo(
    () => buildForest(filtered, edges || []),
    [filtered, edges],
  );

  if (!forest.length) {
    return (
      <div className="web-tree__empty">
        Tree view needs concept clustering — run Refresh clusters once enough
        ads have been analyzed (Phase 2). For now, every ad is its own root.
      </div>
    );
  }

  return (
    <div className="web-tree">
      <ul className="web-tree__roots">
        {forest.map((root) => (
          <TreeNode
            key={root.id}
            node={root}
            onSelect={onSelect}
            selectedId={selectedId}
          />
        ))}
      </ul>
    </div>
  );
}
